import { useCallback } from 'react';
import { useUser } from 'modules/user/userSelector';
import { useAccount } from 'modules/account/accountSelector';
import { G2ReviewTracker } from 'app/utils/g2ReviewTracker';
import G2Api from 'api/g2';
import UserApi from 'api/user';
import { MIN_RECENT_SESSIONS_SHOW_FORM } from 'app/utils/g2ReviewTracker';
import Logger from 'utils/logger';
import dayjs from 'dayjs';
import { useG2ReviewContext } from 'app/context/G2ReviewContext';

function hasEnoughRecentSessions(sessions: { last_access: string }[]): boolean {
  const oneMonthAgo = dayjs().subtract(1, 'month');
  const recentCount = sessions.filter((s) =>
    dayjs(s.last_access).isAfter(oneMonthAgo),
  ).length;
  return recentCount >= MIN_RECENT_SESSIONS_SHOW_FORM;
}

export function useG2ReviewTrigger() {
  const { role } = useUser();
  const account = useAccount();
  const { openG2ReviewModal } = useG2ReviewContext();
  const isEligible =
    (role === 'admin' || role === 'manager') && !account.isTrial;

  const triggerG2Review = useCallback(() => {
    if (!isEligible) return;

    UserApi.getSessions()
      .then((res) => {
        if (!hasEnoughRecentSessions(res.sessions)) return;
        return Promise.all([G2Api.getReviewStatus(), G2Api.getFormDisplayed()]);
      })
      .then((results) => {
        if (results === undefined || results[0].review_exists) return;
        const [, formDisplayed] = results;
        if (
          !G2ReviewTracker.canShow(
            formDisplayed.form_displayed_at,
            formDisplayed.form_display_count,
          )
        )
          return;

        return G2Api.getReviewFormUrl().then(({ url }) => {
          openG2ReviewModal(url);
          G2Api.formDisplayed().catch((e) => Logger.error(e));
        });
      })
      .catch((e) => {
        Logger.error(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEligible, openG2ReviewModal]);

  return { triggerG2Review, isEligible };
}
